// Copyright (c) Ping. All rights reserved.
//
// Web Notes right-PANEL host. Wires the new shared `<NotesPanel>` (a
// single ~409px column with an internal folder/note nav state machine)
// to the live `useNotes(webNotesPersister)` list, owning the SAME
// LOCAL-FIRST mutation path as `NotesRouteHost`:
//
//   onUpsert  → webNotesPersister.put(record)            [local, awaited]
//             → best-effort DeviceGroup `com.ping.note_upsert` send
//   onDelete  → webNotesPersister.delete(id)             [local, awaited]
//             → best-effort DeviceGroup `com.ping.note_delete` send
//
// Notes are DeviceGroup-only personal state (§2.10): envelopes are sent
// into the user's OWN DeviceGroup conversation so the set mirrors across
// their devices. The local write is the source of truth; the send is
// opportunistic and never blocks the UI (offline / no-DG → local-only).
//
// Mount it as a zero-prop component: `<NotesPanelHost />`.

import { useCallback, useMemo } from 'react';

import {
  encodeEnvelopeJsonBytes,
  type NoteRecord,
  type NotesSyncState,
  useNotes,
  useSession,
} from '@ping/ui';
import { NotesPanel } from '@ping/ui/src/notes/NotesPanel';

import { log } from '@core/log/log';
import { webNotesPersister } from '@core/persistence/db';

// Minimal slice of the SDK `MessagingClient` surface we cast onto for
// the DeviceGroup send. Kept local (mirrors NotesRouteHost) so a minor
// SDK bump doesn't ripple through the @ping/ui interface.
interface DeviceGroupSendClient {
  listConversations: () => Promise<
    Array<{ id: Uint8Array; is_device_group: boolean }>
  >;
  send?: (conv: Uint8Array, plaintext: Uint8Array, nowMs?: number) => Promise<unknown>;
}

/** Mint a stable hex `note_id` (16-byte UUID, dashes stripped). */
function makeNoteId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().replace(/-/g, '');
  }
  let out = '';
  for (let i = 0; i < 32; i += 1) out += Math.floor(Math.random() * 16).toString(16);
  return out;
}

export function NotesPanelHost(): JSX.Element {
  const notes = useNotes(webNotesPersister);
  const { client } = useSession();

  // Resolve the user's DeviceGroup conversation id, then `client.send`
  // the encoded envelope into it. Returns `false` on any failure so the
  // caller degrades to local-only (the local write already succeeded).
  const sendToDeviceGroup = useCallback(
    async (envelopeBytes: Uint8Array): Promise<boolean> => {
      if (client === null) return false;
      const sdk = client as unknown as DeviceGroupSendClient;
      if (typeof sdk.send !== 'function') return false;
      try {
        const convs = await sdk.listConversations();
        const dg = convs.find((c) => c.is_device_group);
        if (dg === undefined) return false;
        await sdk.send(dg.id, envelopeBytes);
        return true;
      } catch (err) {
        log.warn({
          event: 'notes.devicegroup.send_failed',
          message: (err as Error).message,
        });
        return false;
      }
    },
    [client],
  );

  const onUpsert = useCallback(
    (note: NoteRecord) => {
      // LOCAL-FIRST: persist immediately so `useNotes` re-renders even if
      // the DeviceGroup send is offline / unavailable.
      void webNotesPersister.put(note);
      const envelope = encodeEnvelopeJsonBytes({
        kind: 'com.ping.note_upsert',
        payload: {
          note_id: note.id,
          title: note.title,
          body: note.body,
          updated_at: note.updatedAt,
          ...(note.tags !== undefined ? { tags: note.tags } : {}),
        },
      });
      void sendToDeviceGroup(envelope);
    },
    [sendToDeviceGroup],
  );

  const onDelete = useCallback(
    (id: string) => {
      void webNotesPersister.delete(id);
      const envelope = encodeEnvelopeJsonBytes({
        kind: 'com.ping.note_delete',
        payload: { note_id: id },
      });
      void sendToDeviceGroup(envelope);
    },
    [sendToDeviceGroup],
  );

  // Sync indicator: mirror to the DeviceGroup only when a client with a
  // `send` method is bootstrapped; otherwise edits are local-only.
  const syncState = useMemo<NotesSyncState>(() => {
    if (client === null) return 'local-only';
    return typeof (client as unknown as DeviceGroupSendClient).send === 'function'
      ? 'synced'
      : 'local-only';
  }, [client]);

  return (
    <NotesPanel
      notes={notes}
      onUpsert={onUpsert}
      onDelete={onDelete}
      makeNoteId={makeNoteId}
      syncState={syncState}
    />
  );
}
